import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useNavigate, useLocation } from "react-router-dom";
import { Truck, LogOut, KeyRound, ExternalLink } from "lucide-react";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import Onboarding from "@/components/Onboarding";
import { NieuweVersieBalk } from "@/components/NieuweVersieBalk";
import { DesktopNav } from "@/components/nav/DesktopNav";
import { BottomBar } from "@/components/nav/BottomBar";
import { MasterBanner } from "@/components/nav/MasterBanner";
import { ErrorBoundary } from "@/components/ErrorBoundary";


const DEFAULT_MASTER = "Master the basics. Wat normaal voelt, controleer je juist — daar glip je het snelst overheen.";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pwOpen, setPwOpen] = useState(false);
  const [masterBasics, setMasterBasics] = useState(DEFAULT_MASTER);
  const [eigenLocatie, setEigenLocatie] = useState("");
  const [eigenAfd, setEigenAfd] = useState("");
  const [counts, setCounts] = useState({});
  const [onbHasNew, setOnbHasNew] = useState(false);

  useEffect(() => {
    const onState = (e) => setOnbHasNew(!!e.detail?.hasNew);
    window.addEventListener("onboarding:state", onState);
    return () => window.removeEventListener("onboarding:state", onState);
  }, []);

  useEffect(() => {
    api.get("/meta").then((r) => {
      if (r.data?.texts?.masterBasics) setMasterBasics(r.data.texts.masterBasics);
      if (r.data?.eigenLocatie) setEigenLocatie(r.data.eigenLocatie);
      if (r.data?.eigenAfdelingen) setEigenAfd(r.data.eigenAfdelingen.map((a) => a.naam).join(" · "));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const fetchCounts = () => api.get("/nav-counts", { signal: controller.signal }).then((r) => setCounts(r.data)).catch(() => {});
    fetchCounts();
    const id = setInterval(fetchCounts, 30000);
    window.addEventListener("nav-counts:refresh", fetchCounts);   // v20-R1: na een vinkje meteen bijwerken
    return () => { clearInterval(id); controller.abort(); window.removeEventListener("nav-counts:refresh", fetchCounts); };
  }, [location.pathname]);

  useEffect(() => {
    if (document.getElementById("tw1-feedback-widget-script")) return;
    const s = document.createElement("script");
    s.id = "tw1-feedback-widget-script";
    s.src = "https://feedback-hub-deploy.emergent.host/widget/tw1-feedback.js";
    s.setAttribute("data-tool", "omcockpit");
    s.setAttribute("data-key", "tw1_7af316af_398ef0db9686ad91e11e5b16efd7fc0abe157af1");
    document.body.appendChild(s);
  }, []);

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <div data-testid="sticky-kop" className="sticky top-0 z-50">
        <header className="bg-zinc-950 text-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-md bg-blue-600 flex items-center justify-center">
                <Truck className="h-5 w-5" strokeWidth={2.2} />
              </div>
              <div className="leading-tight">
                <div className="font-heading font-black tracking-tight">OM-Cockpit · TW1</div>
                <div className="text-[10px] text-zinc-400 uppercase tracking-widest font-semibold capitalize">
                  {user.username} · {user.role === "om" ? "OM" : user.role === "bedrijfsleider" ? `Bedrijfsleider${eigenLocatie ? ` · ${eigenLocatie}` : ""}` : user.role === "afdeling" ? (eigenAfd || "Afdeling") : "Oversight"}
                </div>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-4">
              <a
                data-testid="link-daily-ops"
                href="https://daily-ops.nl"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-zinc-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-600 rounded-sm px-1"
              >
                Daily Ops <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <a
                data-testid="link-hub"
                href="https://truckwash-ops.nl"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-zinc-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-600 rounded-sm px-1"
              >
                Hub <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <span className="hidden sm:block h-4 w-px bg-white/10" />
              <button
                data-testid="change-password-btn"
                onClick={() => setPwOpen(true)}
                className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-zinc-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-600 rounded-sm px-1"
              >
                <span className="relative"><KeyRound className="h-4 w-4" />
                  {user.usingDefault && <span data-testid="default-password-hint" title="Stel een eigen wachtwoord in" className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-amber-400" />}
                </span> <span className="hidden sm:inline">Wachtwoord</span>
              </button>
              <button
                data-testid="logout-btn"
                onClick={() => { logout(); navigate("/"); }}
                className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-zinc-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-600 rounded-sm px-1"
              >
                <LogOut className="h-4 w-4" /> Uitloggen
              </button>
            </div>
          </div>
          <DesktopNav user={user} counts={counts} onbHasNew={onbHasNew} />
        </header>

        <MasterBanner tekst={masterBasics} />
      </div>

      <Onboarding />


      <NieuweVersieBalk />
      <main className="tw1-main max-w-7xl mx-auto px-4 md:px-8 pt-6 md:pb-28">
        <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>
      </main>

      <BottomBar user={user} counts={counts} onbHasNew={onbHasNew} wachtwoordHint={!!user.usingDefault} onWachtwoord={() => setPwOpen(true)} onUitloggen={() => { logout(); navigate("/"); }} />
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
